import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, XCircle, Search, Award } from "lucide-react";
import api from "@/lib/api";
import { useCms } from "@/lib/cms";

export default function Verify() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const { certId } = useParams();
  const [q, setQ] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Unchanged — same endpoint, same request/response shape as before.
  const doVerify = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/verify/${id}`);
      setResult(data);
    } catch { setResult({ valid: false }); }
    setLoading(false);
  };

  useEffect(() => { if (certId) doVerify(certId); }, [certId]);

  return (
    <div style={{ backgroundColor: "#F8FAFC" }}>
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-20 sm:py-24">
        <div className="fade-up text-center">
          <div className="w-12 h-12 rounded-full grid place-items-center mx-auto mb-5" style={{ backgroundColor: "#2D7A7314" }}>
            <Award className="w-5 h-5" style={{ color: "#2D7A73" }} aria-hidden="true" />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#3B68E7" }}>Verify</div>
          <h1
            className="rebrand-serif font-bold tracking-tight"
            style={{ fontSize: "clamp(2.25rem, 3vw + 1rem, 3rem)", color: "#1A202C" }}
          >
            Certificate authenticity.
          </h1>
          <p className="mt-4" style={{ color: "#64748B" }}>Enter a certificate ID to instantly confirm it was issued by {brandName}.</p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doVerify(q)}
              placeholder="e.g. EDL-A1B2C3D4E5"
              data-testid="verify-input"
              className="w-full pl-11 pr-4 outline-none text-sm font-mono transition-all"
              style={{ height: 54, borderRadius: 16, border: "1px solid #DCE4EC" }}
              onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
              onBlur={(e) => { e.target.style.borderColor = "#DCE4EC"; e.target.style.boxShadow = "none"; }}
            />
          </div>
          <button
            onClick={() => doVerify(q)}
            disabled={loading}
            data-testid="verify-submit"
            className="text-white text-sm font-semibold shrink-0 inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] disabled:opacity-60"
            style={{ height: 54, paddingLeft: 28, paddingRight: 28, backgroundColor: "#3B68E7", borderRadius: 50, boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
          >
            {loading ? (
              <>
                <span className="w-3.5 h-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Checking…
              </>
            ) : (
              "Verify"
            )}
          </button>
        </div>

        {result && (
          <div
            className="mt-8 p-8 fade-up"
            style={{
              borderRadius: 24,
              backgroundColor: result.valid ? "#ECFDF5" : "#FEF2F2",
              border: `1px solid ${result.valid ? "#A7F3D0" : "#FECACA"}`,
            }}
            data-testid="verify-result"
          >
            {result.valid ? (
              <div className="flex gap-4">
                <ShieldCheck className="w-8 h-8 flex-shrink-0" style={{ color: "#16A34A" }} aria-hidden="true" />
                <div>
                  <div className="font-semibold" style={{ color: "#16A34A" }}>
                    This certificate is valid
                    <span className="ml-2 text-xs font-medium uppercase tracking-wider" style={{ color: "#16A34A99" }}>
                      {result.certificate.certificate_type === "podcast" ? "Podcast Guest Recognition Certificate" : "Certificate of Completion"}
                    </span>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                    {result.certificate.certificate_type === "podcast" ? (
                      <>
                        <Info k="Guest" v={result.certificate.teacher_name} />
                        <Info k="Podcast Series" v={result.certificate.podcast_series} />
                        <Info k="Episode Title" v={result.certificate.episode_title} />
                        {result.certificate.episode_number && <Info k="Episode No." v={result.certificate.episode_number} />}
                        {result.certificate.episode_theme && <Info k="Theme / Topic" v={result.certificate.episode_theme} />}
                        <Info k="Date" v={result.certificate.date} />
                        <Info k="Certificate ID" v={<span className="font-mono">{result.certificate.certificate_id}</span>} />
                      </>
                    ) : (
                      <>
                        <Info k="Teacher" v={result.certificate.teacher_name} />
                        <Info k="School" v={result.certificate.school_name} />
                        <Info k="Training" v={result.certificate.training_name} />
                        <Info k="Date" v={result.certificate.date} />
                        <Info k="Certificate ID" v={<span className="font-mono">{result.certificate.certificate_id}</span>} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-4">
                <XCircle className="w-8 h-8 flex-shrink-0" style={{ color: "#DC2626" }} aria-hidden="true" />
                <div>
                  <div className="font-semibold" style={{ color: "#DC2626" }}>Certificate not found</div>
                  <div className="text-sm mt-1" style={{ color: "#64748B" }}>Check the ID and try again. If you believe this is an error, contact {brandName}.</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Info = ({ k, v }) => (
  <div>
    <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#94A3B8" }}>{k}</div>
    <div className="font-medium mt-0.5" style={{ color: "#1A202C" }}>{v}</div>
  </div>
);
